import { forwardRef } from 'react';
import type { InputNumberRef } from '@rc-component/input-number';
import { NumberInput } from './NumberInput';
import type { NumberInputProps } from './NumberInput';
import { colors } from '@/theme/colors';

/**
 * Props {@link MUAInput}.
 *
 * Inherits semua props {@link NumberInput} kecuali `numberFormat`
 * yang sudah dikunci sesuai spek MUA. (Stepper sudah dikunci mati di base.)
 */
export type MUAInputProps = Omit<NumberInputProps, 'numberFormat'>;

/**
 * DERIVATIF: Textbox untuk nilai dalam Mata Uang Asal (MUA / mata uang asing).
 *
 * Spek:
 * - Base: {@link NumberInput} (rata kanan, separator id-ID, 0→'', - prefix).
 * - 2 desimal (mis. `16.524,37`).
 * - Tidak ada stepper.
 * - **Background biru muda** (`colors.mataUangBg = #E3F2FD`) untuk pembedaan
 *   visual dari input IDR. Konsisten dengan Booku Lama.
 * - **Saat disabled**: background tetap berwarna biru namun lebih buram
 *   (`colors.mataUangBgDisabled`) — mempertahankan identitas visual MUA
 *   sambil menandakan field tidak dapat diedit.
 *
 * Contoh:
 * - 16524.37   → tampil `16.524,37`
 * - -16524.37  → tampil `-16.524,37`
 * - 0          → tampil `` (kosong)
 *
 * Pemakaian:
 * ```tsx
 * <MUAInput value={nilaiMUA} onChange={setNilaiMUA} />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/textbox-pattern.md`
 */
export const MUAInput = forwardRef<InputNumberRef, MUAInputProps>(
  function MUAInput({ style, disabled, ...rest }, ref) {
    const bg = disabled ? colors.mataUangBgDisabled : colors.mataUangBg;
    return (
      <NumberInput
        ref={ref}
        numberFormat="decimal"
        disabled={disabled}
        style={{ backgroundColor: bg, ...style }}
        {...rest}
      />
    );
  }
);
